<script lang="ts">
  import { onMount } from 'svelte';
  import AppShell from '../components/AppShell.svelte';
  import KpiCard from '../components/KpiCard.svelte';
  import ChartBars from '../components/ChartBars.svelte';
  import { fetchFunnelStats, type FunnelStats } from '../lib/funnels-api';
  import { fmtInt, fmtPct, fmtUah } from '../lib/format';

  let { params }: { params: { slug?: string } } = $props();
  const slug = $derived(params?.slug ?? '');

  let stats = $state<FunnelStats | null>(null);
  let loading = $state(true);
  let err = $state<string | null>(null);

  let rangeDays = $state(30);
  let tariffSku = $state('');
  let launchSlug = $state('');

  async function reload() {
    if (!slug) return;
    loading = true;
    try {
      stats = await fetchFunnelStats(slug, rangeDays, {
        tariffSku: tariffSku.trim() || undefined,
        launchSlug: launchSlug.trim() || undefined,
      });
      err = null;
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    } finally {
      loading = false;
    }
  }
  onMount(reload);

  function fmtDuration(sec: number | null): string {
    if (sec === null) return '—';
    if (sec < 3600) return `${Math.round(sec / 60)} хв`;
    if (sec < 24 * 3600) return `${Math.round(sec / 3600)} г`;
    const d = Math.floor(sec / (24 * 3600));
    const h = Math.round((sec % (24 * 3600)) / 3600);
    return `${d} д ${h} г`;
  }
</script>

<AppShell active="funnels">
  <div class="mx-auto max-w-[1200px]">
    <div class="text-xs text-[var(--color-text-muted)] mb-2">
      <a href="#/funnels" class="text-[var(--color-pu)] no-underline hover:underline">Воронки</a> ›
      <span>{stats?.funnelName ?? slug}</span>
    </div>

    {#if loading}
      <div class="py-20 text-center text-sm text-[var(--color-text-muted)]">Завантаження…</div>
    {:else if err}
      <div class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{err}</div>
    {:else if stats}
      <header class="mb-5 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 class="text-2xl font-bold leading-tight">{stats.funnelName}</h1>
          <p class="mt-1 text-[12px] text-[var(--color-text-muted)]">
            slug: <code>{stats.funnelSlug}</code> · {stats.stages.length} стадій · конверсія entry → purchase: <strong class="text-emerald-600">{fmtPct(stats.conversionPct)}</strong>
          </p>
        </div>
        <div class="flex items-end gap-2">
          <div class="inline-flex rounded-lg border border-[var(--color-border-3)] bg-white p-0.5 text-[11px] font-semibold mr-1 self-end">
            <a href="#/funnels/{slug}/pipeline" class="rounded-md px-3 py-1.5 text-[var(--color-text-muted)] no-underline hover:bg-[var(--color-bg-3)]">Pipeline</a>
            <span class="rounded-md bg-[var(--color-pu)] px-3 py-1.5 text-white">Статистика</span>
          </div>
          <label class="block"><div class="text-[9px] font-semibold uppercase text-[var(--color-text-muted-2)] mb-1">Період</div>
            <select bind:value={rangeDays} onchange={reload} class="rounded-md border border-[var(--color-border-3)] px-2 py-1.5 text-[11px]">
              <option value={7}>7 днів</option><option value={30}>30 днів</option><option value={90}>90 днів</option><option value={365}>рік</option>
            </select></label>
          <label class="block"><div class="text-[9px] font-semibold uppercase text-[var(--color-text-muted-2)] mb-1">Tariff SKU</div>
            <input bind:value={tariffSku} onchange={reload} placeholder="усі" class="w-[110px] rounded-md border border-[var(--color-border-3)] px-2 py-1.5 text-[11px] font-mono" /></label>
          <label class="block"><div class="text-[9px] font-semibold uppercase text-[var(--color-text-muted-2)] mb-1">Launch slug</div>
            <input bind:value={launchSlug} onchange={reload} placeholder="усі" class="w-[110px] rounded-md border border-[var(--color-border-3)] px-2 py-1.5 text-[11px] font-mono" /></label>
        </div>
      </header>

      <!-- KPI -->
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <KpiCard eyebrow="Entry" value={fmtInt(stats.entryCount)} />
        <KpiCard eyebrow="Purchase" value={fmtInt(stats.purchaseCount)} deltaPct={stats.conversionPct} deltaSuffix=" %" />
        <KpiCard eyebrow="Revenue" value={fmtUah(stats.revenue30dCents, { compact: true })} />
        <KpiCard eyebrow="Avg time to purchase" value={fmtDuration(stats.avgTimeToPurchaseSec)} />
      </div>

      <!-- Stage list -->
      <div class="rounded-xl border border-[var(--color-border-3)] bg-white p-4 mb-5">
        <div class="text-[13px] font-bold mb-3">Конверсія по стадіях</div>
        <div class="flex flex-col gap-2.5">
          {#each stats.stages as s, i (s.slug)}
            {@const widthPct = Math.max(2, s.pctOfEntry)}
            <div class="grid items-center gap-3.5 rounded-lg border border-[var(--color-border-3)] px-4 py-3" style="grid-template-columns: 36px 1fr 240px 120px 90px">
              <div class="grid h-8 w-8 place-items-center rounded-full bg-[var(--color-pu-light)] text-[var(--color-pu-3)] font-bold text-[13px]">{i + 1}</div>
              <div>
                <div class="text-[13px] font-semibold">{s.name}</div>
                <div class="text-[10.5px] text-[var(--color-text-muted)] mt-0.5">slug: <code>{s.slug}</code></div>
              </div>
              <div class="h-3 overflow-hidden rounded-full bg-[var(--color-bg-2)]">
                <div class="h-full rounded-full" style="width: {widthPct}%; background: linear-gradient(90deg, var(--color-pu), var(--color-pu-2));"></div>
              </div>
              <div class="text-right">
                <div class="text-[13px] font-bold tabular-nums">{fmtInt(s.count)}</div>
                <div class="text-[10px] text-[var(--color-text-muted)] font-medium">{fmtPct(s.pctOfEntry)} від entry</div>
              </div>
              <div class="text-center text-[12px] font-bold rounded-lg px-2 py-1 {s.pctOfPrev >= 75 ? 'bg-emerald-100 text-emerald-700' : s.pctOfPrev >= 40 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}">
                {fmtPct(s.pctOfPrev)}
              </div>
            </div>
          {/each}
        </div>
      </div>

      <div class="grid grid-cols-2 gap-3.5">
        <ChartBars
          title="Розподіл часу до купівлі"
          sub="≤2г · 12г · 1д · 2д · 3д · 7д · 7д+"
          bars={stats.timeToPurchaseHistogram}
        />

        <div class="rounded-xl border border-[var(--color-border-3)] bg-white p-4">
          <div class="text-[13px] font-bold mb-1">Когорти по тижню</div>
          <div class="text-[10.5px] text-[var(--color-text-muted)] mb-2">Розмір + конверсія в покупку</div>
          {#if stats.cohorts.length === 0}
            <div class="py-8 text-center text-xs text-[var(--color-text-muted)]">Поки немає достатньо даних</div>
          {:else}
            <table class="w-full text-[11px]">
              <thead><tr class="text-[10px] uppercase text-[var(--color-text-muted-2)]">
                <th class="text-left py-1.5">Когорта</th>
                <th class="text-right py-1.5">N</th>
                {#each stats.stages.slice(0, 4) as st (st.slug)}<th class="text-right py-1.5">{st.slug.slice(0, 6)}</th>{/each}
                <th class="text-right py-1.5">Conv</th>
              </tr></thead>
              <tbody>
                {#each stats.cohorts as c (c.cohortDay)}
                  <tr class="border-t border-[var(--color-border-3)]">
                    <td class="py-1.5 text-[var(--color-text-muted)]">{c.cohortDay}</td>
                    <td class="text-right font-bold tabular-nums">{fmtInt(c.size)}</td>
                    {#each c.stageCounts.slice(0, 4) as cnt (cnt)}<td class="text-right tabular-nums">{cnt}</td>{/each}
                    <td class="text-right"><span class="rounded-full bg-[var(--color-pu-light)] px-2 py-0.5 font-bold text-[var(--color-pu-3)]">{fmtPct(c.conversionPct)}</span></td>
                  </tr>
                {/each}
              </tbody>
            </table>
          {/if}
        </div>
      </div>
    {/if}
  </div>
</AppShell>
